import { odataList } from "services/odataApi";

export namespace ResultsModuleHelpers {
  export const internalResultsLink = (
    projectId: number,
    path: string,
    urlParams?: Record<string, string | number | (string | number)[] | undefined | null>,
  ): string => {
    const currentSearchParams = new URLSearchParams(window.location.search);
    const mergedParams = new URLSearchParams();

    if (urlParams) {
      for (const [key, value] of Object.entries(urlParams)) {
        if (value === undefined || value === null) {
          continue;
        }

        if (Array.isArray(value)) {
          const sanitizedArray = value
            .filter((v): v is string | number => v !== undefined && v !== null)
            .map(String);

          if (sanitizedArray.length > 0) {
            mergedParams.set(key, `[${sanitizedArray.join(",")}]`);
          }
        } else {
          mergedParams.set(key, String(value));
        }
      }
    }

    for (const [key, value] of currentSearchParams.entries()) {
      if (!mergedParams.has(key)) {
        mergedParams.set(key, value);
      }
    }

    const queryString = mergedParams.toString();
    return `/results/${projectId}/${path}${queryString ? `?${queryString}` : ""}`;
  };

  export const getKeys = (dictionary: { [key in number]: boolean }) =>
    Object.keys(dictionary).map((a) => dictionary[Number(a)]);

  export const getSubKeys = (dictionary: { [key in number]: { [key in number]: boolean } }) =>
    Object.keys(Object.values(dictionary)).map((a) => dictionary[Number(a)]);

  export const getTargetsReqBuilder = (projectId: number) =>
    odataList("getTargets")
      .select("id", "question", "systemQuestionOption")
      .filter("projectId", "eq", projectId)
      .expandAndSelect("question", null, "questionTitle")
      .expandAndSelect("questionOption", null, "optionText")
      .expandAndSelect(
        "systemQuestionOption",
        (a) => a.expandAndSelect("systemQuestion", null, "questionLabel"),
        "answerLabel",
      );

  export const getProjectScheduleReqBuilder = (projectId: number) =>
    odataList("getProjectSchedules").filter("projectId", "eq", projectId).orderBy("createdAt");

  export const getPanelTypesReqBuilder = (projectId: number) =>
    odataList("getProjectSchedules")
      .select("id", "regionId", "isCustomerPanel")
      .filter("projectId", "eq", projectId)
      .filter("surveyRunId", "ne", null);

  export const getChartColour = (index: number, projectId: number | null = null) => {
    return hslColor(Number(index.toString().split("").reverse().join("")), projectId ?? index).rgbString;
  };
}

const rgbToHsl = (r: number, g: number, b: number) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d === 0) h = 0;
  else if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else if (max === b) h = (r - g) / d + 4;
  const l = (min + max) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  return { h: h * 60, s, l };
};

const hslColor = (i: number, n: number) => {
  // Calculate the number of divisions (cuts) along each axis
  const numCuts = Math.ceil(Math.cbrt(n));
  const pieceLength = Math.floor(255 / numCuts);

  // Calculate the RGB component for each axis based on the index
  // Formula: FLOOR( (FLOOR(i/numCuts^pos) %numCuts) *pieceLength + pieceLength/2)
  const bx = Math.floor((i % numCuts) * pieceLength + pieceLength / 2);
  const gx = Math.floor((Math.floor(i / numCuts) % numCuts) * pieceLength + pieceLength / 2);
  const rx = Math.floor((Math.floor(i / (numCuts * numCuts)) % numCuts) * pieceLength + pieceLength / 2);

  const hsl = rgbToHsl(rx < 25 ? 25 : rx, gx < 20 ? 20 : gx, bx);
  const rgbString = `rgb(${rx}, ${gx}, ${bx})`;
  const hslString = `hsl(${hsl.h.toFixed(3)}, ${hsl.s.toFixed(2)}%, ${hsl.l.toFixed(2)}%)`;
  // return hslString;

  // Return the RGB color string
  return {
    rgb: { r: rx, g: gx, b: bx },
    hsl: hsl,
    rgbString: rgbString,
    hslString: hslString,
  };
};
